// managers/heatmap/HeatmapManager.ts
import type { Map as MapType, GeoJSONSource } from "maplibre-gl";
import type { MapEventBus } from "../../core/MapEventBus";
import { SOURCES } from "../../sources";
import type { HeatmapPoint } from "../../types";

export class HeatmapManager {
  private points: HeatmapPoint[] = [];
  private pendingFlush = false;
  private rafId: number | null = null;

  constructor(
    private map: MapType,
    private eventBus: MapEventBus,
  ) {}

  // PUBLIC API — called by HeatmapUpdater via the skopa/heatmap:batch WS topic.
  updateHeatmapBatch(points: HeatmapPoint[]) {
    this.points = points ?? [];
    this.scheduleFlush();
  }

  clear() {
    this.points = [];
    this.scheduleFlush();
  }

  // Coalesce updates into a single setData on the next frame; retry until the
  // style/source is ready.
  private scheduleFlush() {
    if (this.pendingFlush) return;
    this.pendingFlush = true;
    this.rafId = requestAnimationFrame(() => {
      this.pendingFlush = false;
      this.rafId = null;
      this.flushUpdates();
    });
  }

  private flushUpdates() {
    const source = this.map.getSource(SOURCES.HEATMAP) as
      | GeoJSONSource
      | undefined;
    if (!source || !this.map.isStyleLoaded()) {
      this.scheduleFlush();
      return;
    }

    const features: GeoJSON.Feature[] = this.points.map((p, index) => ({
      id: index,
      type: "Feature",
      geometry: { type: "Point", coordinates: [p.lon, p.lat] },
      properties: {
        weight: typeof p.weight === "number" ? p.weight : 0.5,
      },
    }));

    try {
      source.setData({ type: "FeatureCollection", features });
    } catch (err) {
      console.warn("MapLibre heatmap flush deferred:", err);
      this.scheduleFlush();
    }
  }

  public getPointCount(): number {
    return this.points.length;
  }

  destroy() {
    if (this.rafId !== null) cancelAnimationFrame(this.rafId);
    this.points = [];
  }
}
